import { transformCostData } from "./data-to-ui-adapters/data-transform-functions.js";

// TODO: Possibly split this up into "clientData" and "uiState" 
// - uiState concerns fields like "currentStageIndex", "currentBookingIndex" 
// - clientData refers to "serverLetter" (server home), "config" (all data related to client), partyMembers, subUnits, units,

// Freeze the global state object root - can still set properites within "data" property
const GlobalState = Object.freeze({
    appState: {
        currentBookingIndex: 0,
    },
    clientData: {
        csrf         : null,
        server       : null,
        config       : null,
        multisiteModule: null,

        currentStageId: null, // TODO: move this into appState but will need to change "initState" in app.js and get/set in this file too
        uiData      : {
            partyMembers: [],    
            subUnits    : null,
            units       : [],
            stages      : []
        },
        modules     : {
            multipleBookings: false
        }
    },
    bookingDataArray     : [ // can have multiple bookings
        {
            site                    : null,
            category                : null,
            unit                    : null,
            sub_unit                : null,
            party_size              : 0,
            party_members           : [],
            arrival_date            : null,
            departure_date          : null,
            period                  : 0,
            costs                   : {},
            addons                  : {},
            using_pets_filter       : false,
            using_pitch_filter      : false,
            sub_unit_pitch_type     : null,
            restrict_sub_unit_period: null,
            av_sub_units            : [],
            av_pitch_types          : [],
            chosen_payment_type     : null,
            availability_type        : null,
            // xcover related
            can_refund_booking      : false,
            has_run_refund_quote    : false,
            use_booking_protect     : false
        }
    ]
});

// NOTE: the unit objects obtained from smarty are incomplete until the unit has been selected and an extended data object is returned, so they need to be patched
// with data used in the summarys
const PATCHABLE_UNIT_FIELDS = [
    'thumbnail',
    'city',
    'state',
    'address',
    'availability_type',
    'latitude',
    'longitude'
];

// export let GlobalState = null;
const initState = (initialState = {}) => {
    Object.assign(GlobalState.clientData, initialState);
}


/**
 * Set the value of global state found at the given key 
 * @param {*} key - must be a property that already exists on the object
 * @param {*} object - the object to set to state
 * @returns 
 */
const setClientDataProp = (key, value) => {
    const target = GlobalState.clientData[key];

    if (!(key in GlobalState.clientData)) {
        console.warn(`Property "${key}" does not exist in GlobalState.clientData`);
        return;
    }

    // If the value is an array, do a regular assignment
    if (Array.isArray(target)) {
        if (!Array.isArray(value)) {
            console.warn(`Expected array for "${key}", received`, value);
            return;
        }

        GlobalState.clientData[key] = value;
        return;
    }

    // If the value is an object, assign it to the target
    if (typeof target === 'object' && target !== null) {
        if (typeof value !== 'object' || value === null) {
            console.warn(`Expected object for "${key}", received`, value);
            return;
        }

        Object.assign(target, value);
        return;
    }

    // Primitives → replace
    GlobalState.clientData[key] = value;
}

/**
 * Used to set a property by key in state for the active booking
 * @param {string} key 
 * @param {any} value 
 * @returns 
 */
const setBookingStateProp = (key, value) => {
    const bookingIndex = getCurrentBookingIndex();
    if (!GlobalState.bookingDataArray[bookingIndex]) {
        console.warn(`Property: ${key} does not exist in Booking Data`)
        return;
    }

    GlobalState.bookingDataArray[bookingIndex][key] = value;
}

/**
 * Used to set multiple 
 * @param {*} object 
 */
function setBookingState(object) {
    for (const [key, val] of Object.entries(object)) {
        setBookingStateProp(key, val);
    }
}

function getBookingStateProp(key) {    
    const value = GlobalState.bookingDataArray[getCurrentBookingIndex()][key];
    if (value == undefined) {
        console.error(`No value found for prop ${key} in booking state`);
        return;
    }

    return value;
}

/**
 * Bulk insert cost data for the active booking
 * @param {*} data 
 */
function setCostState(data) {
    // Ensure GlobalState.bookingDataArray[GlobalState.appState.currentBookingIndex] exists
    const currentBookingIndex = getCurrentBookingIndex();
    if (!GlobalState.bookingDataArray[currentBookingIndex]) 
        GlobalState.bookingDataArray[currentBookingIndex] = {};

    // Copy all costs and relevant info into GlobalState
    GlobalState.bookingDataArray[currentBookingIndex].costs = transformCostData(data);
}

function resetCostState() {
    const currentBookingIndex = GlobalState.appState.currentBookingIndex;
    if (!GlobalState.bookingDataArray[currentBookingIndex]) 
        GlobalState.bookingDataArray[currentBookingIndex] = {};

    const {
        allBookingsTotal,
        allBookingsTotalToPay,
        allBookingsTotalToPayUnformatted,
        allBookingsTotalUnformatted
    } = GlobalState.bookingDataArray[currentBookingIndex].costs

    const currencySymbol = GlobalState.clientData.uiData.currency || '';
    const zeroCostString = `${currencySymbol}0.00`;

    GlobalState.bookingDataArray[currentBookingIndex].costs = {
        base: 0,
        baseUnformatted: 0,
        baseWithParty: 0,
        baseNormal: 0,
        baseNormalUnformatted: 0,
        baseDiscount: 0,
        baseDiscountUnformatted: 0,

        currencySymbol: currencySymbol,

        total: 0,
        totalUnformatted: zeroCostString,
        totalNet: 0,
        totalNetFormatted: zeroCostString,
        totalVat: 0,
        totalVatFormatted: zeroCostString,
        totalFormatted: zeroCostString,
        totalToPay: 0,
        totalToPayFormatted: zeroCostString,
        totalToPayUnformatted: 0,

        agent: 0,
        agentFormatted: zeroCostString,

        discount: 0,
        discountFormatted: zeroCostString,
        discountCode: '',

        partyMembers: 0,
        partyMembersUnformatted: 0,
        partyMembersBreakdown: [],
        partySize: 0,

        addons: 0,
        addonsUnformatted: 0,
        addonsBreakdown: [],

        deposit: 0,
        depositFormatted: zeroCostString,
        depositUnformatted: 0,

        balance: 0,
        balanceFormatted: zeroCostString,
        balanceDueDate: null,

        freeOfCharge: false,
        bookingInfo: {},

        // Totals for Multiple bookings
        allBookingsTotal: allBookingsTotal || zeroCostString,
        allBookingsTotalToPay: allBookingsTotalToPay || zeroCostString,
        allBookingsTotalToPayUnformatted: allBookingsTotalToPayUnformatted ?? 0,
        allBookingsTotalUnformatted: allBookingsTotalUnformatted ?? 0
    }
}

// BOOKING 
const setCurrentBookingIndex = (index) => {
    const bookingArrayLength = GlobalState.bookingDataArray.length;

    if (index < 0 || index >= bookingArrayLength) {
        console.warn(
            `Invalid booking index: ${index}. Valid range: 0–${bookingArrayLength - 1}`
        );
        return false;
    }

    GlobalState.appState.currentBookingIndex = index;
    return true;
}

const getCurrentBookingIndex = () => GlobalState.appState.currentBookingIndex;

const initNewBooking = ({
    site                = null,
    category            = null,
    unit                = null,
    party_members       = [],
    arrival_date        = null,
    departure_date      = null,
    period              = 0,
    costs               = {}
}) => {
    const newBooking = {
        site,
        category,
        unit,
        party_members,
        arrival_date,
        departure_date,
        period,
        costs
    };

    GlobalState.bookingDataArray.push(newBooking);
    
    const newIndex             = GlobalState.bookingDataArray.length - 1;
    
    setCurrentBookingIndex(newIndex);
    setCurrentStageId(getStages()[0].id);

    resetCostState();

    // return the new bookings data incase we want to use it or read from it
    return newBooking;
}

const deleteBookingAtIndex = (index) => {
    const array = GlobalState.bookingDataArray;

    // Validate index
    if (index < 0 || index >= array.length) {
        console.warn(
            `deleteBookingAtIndex: index out of bounds. Index: ${index}, length: ${array.length}`
        );
    }
    
    // Remove the booking from the array
    array.splice(index, 1);

    // Adjust currentBookingIndex if necessary
    if (GlobalState.appState.currentBookingIndex >= array.length) {
        GlobalState.appState.currentBookingIndex = array.length - 1; // last booking
    }
}

/**
 * Gets the booking data found at the current booking index 
 * @returns currentBookingData
 */
const getBookingState = () => {
    if (!GlobalState.bookingDataArray[GlobalState.appState.currentBookingIndex])
        GlobalState.bookingDataArray[GlobalState.appState.currentBookingIndex] = {};
    return structuredClone(GlobalState.bookingDataArray[GlobalState.appState.currentBookingIndex]);
}

const getBookingStateArray = () => {
    return structuredClone(GlobalState.bookingDataArray);
}

const getClientData = () => {
    return structuredClone(GlobalState.clientData);
}

// STAGES
const getStages = () => structuredClone(GlobalState.clientData.uiData.stages);

const getCurrentStageId = () => GlobalState.clientData.currentStageId;
const setCurrentStageId = (stageId) => GlobalState.clientData.currentStageId = stageId;


// CLIENT DATA ACCESSORS
const getUnitData = (unitId) => {
    const unit = GlobalState.clientData.units.find(unit => parseInt(unit.id, 10) == parseInt(unitId, 10));
    return unit ? structuredClone(unit) : null;
};

const getCategoryData = (categoryId) => {
    return GlobalState.clientData.categories?.find(category => parseInt(category.id, 10) == parseInt(categoryId, 10)) ?? null;
};

const getSiteData = (siteId) => {
    return GlobalState.clientData.sites?.find(site => parseInt(site.id, 10) == parseInt(siteId, 10)) ?? null;
};

/**
 * Resolves the site name a unit belongs to, via the unit's category
 * @param {object} unit - a unit object containing a category_id
 * @returns {string} the site name, or an empty string if not found
 */
const getSiteNameForUnit = (unit) => {
    if (!unit) return '';

    const category = getCategoryData(unit.category_id);
    if (!category) return '';

    const site = getSiteData(category.site_id);
    return site?.name ?? '';
};

const isMultisiteEnabled = () => parseInt(GlobalState.clientData.multisiteModule, 10) === 1;

const getServer = () => GlobalState.clientData.serverLetter;

const setUnitData = (id, object = {}) => {
    Object.assign(GlobalState.clientData.units[id], object);
}

/**
 * Patch unit data by ID - only allows certain fields to be patched - unit data dosn't get fully loaded until selection
 * @param {number} id 
 * @param {object} patchData
 * @returns 
 */
const patchUnitDataById = (id, patch = {}) => {
    const unit = GlobalState.clientData.units.find(unit => unit.id == id);

    if (!unit) {
        console.warn(`Unit with id "${id}" not found`);
        return false;
    }

    PATCHABLE_UNIT_FIELDS.forEach(field => {
        if (patch[field] !== undefined) {
            unit[field] = patch[field];
            return;
        }
        console.warn(`Field "${field}" not found in patch data, skipping`);
    });

    return true;
}

const getCsrfToken = () => GlobalState.clientData.csrf;


/**
 * Returns the current state object - not intended for use outside of debugging
 * @returns the entire state object
 */
const logState = () => console.log(GlobalState);

export { 
    // set/init state
    setClientDataProp,
    initState,

    // Client data
    getClientData,
    getStages, 
    getCurrentStageId,
    setCurrentStageId,

    getCurrentBookingIndex,

    getUnitData,
    setUnitData, // Used for preset bookings only
    getCategoryData,
    getSiteData,
    getSiteNameForUnit,
    isMultisiteEnabled,
    getCsrfToken,
    getServer,
    patchUnitDataById,

    // booking state - inc cost
    initNewBooking,
    setBookingState,
    setBookingStateProp,
    getBookingStateProp,

    getBookingState,
    getBookingStateArray,
    setCostState,
    resetCostState,
    deleteBookingAtIndex,

    logState
};
